import { useState, useEffect } from "react";
import { EventBus } from "@engine/EventBus.js";
import { getFeedBuffer } from "@engine/Feed.js";

export interface CombatLogEntry {
  /** Stable React key — monotonically increasing across the session. */
  readonly id: number;
  readonly message: string;
  /** Wall-clock ms when the message hit the feed. */
  readonly pushedAt: number;
}

let nextId = 0;

/**
 * Returns a rolling log of combat messages, newest last. Each entry carries
 * a `pushedAt` timestamp so the floating overlay variant of `CombatLog` can
 * fade messages out after a hold + fade window. The persistent panel
 * variant (desktop right-rail) ignores the timestamp.
 *
 * Hydration: pre-existing buffer entries get `pushedAt: 0` so the floating
 * overlay treats them as already faded and never flashes a wall of stale
 * history on mount.
 */
export function useCombatLog(maxEntries = 50): readonly CombatLogEntry[] {
  const [log, setLog] = useState<CombatLogEntry[]>(() =>
    [...getFeedBuffer()].slice(-maxEntries).map((message) => ({
      id: nextId++,
      message,
      pushedAt: 0,
    })),
  );

  useEffect(() => {
    const unsub = EventBus.on("ui:combat_log_push", ({ message }) => {
      setLog((prev) => [
        ...prev.slice(-(maxEntries - 1)),
        { id: nextId++, message, pushedAt: Date.now() },
      ]);
    });
    return unsub;
  }, [maxEntries]);

  return log;
}
